import { makeFetchData } from "api/makeApiRequestType/makeFetchData";
import { Term, validateTerms } from "api/getUserProfile";
import { validate, ValidationCallback } from "validation/validate";

interface RawApiOutput {
	settings: {
		timezone: string;
		locationDistrict: string;
		locationCountryCode: string;
		displayName: string;
		mentorProfileAboutMe: string;
		defaultCurrencyCode: string;
		skypeId: string;
		defaultGoogleMeetLink: string;
		hasPaidZoomAccount: boolean;

		paymentProvidersMBWay: string;
		paymentProvidersWisetag: string;
		paymentProvidersCustom: string;
	};
	links: {
		hasLinkedZoomAccount: boolean;
	};
	profileData: {
		isMentor: boolean;
		locationCountryName: string;
		terms: Term[];
		icalKey: string;
	};
}

export type Output = RawApiOutput["settings"] &
	RawApiOutput["links"] &
	RawApiOutput["profileData"];

const validateSettings: ValidationCallback<RawApiOutput["settings"]> = (
	input,
	{ flag, checkField },
	auth
) => {
	checkField("timezone", { type: "string" });
	checkField("locationDistrict", { type: "string" });
	checkField("locationCountryCode", { type: "string" });
	checkField("displayName", { type: "string" });
	checkField("mentorProfileAboutMe", { type: "string" });
	checkField("defaultCurrencyCode", { type: "string" });
	checkField("skypeId", { type: "string" });
	checkField("defaultGoogleMeetLink", { type: "string" });
	checkField("hasPaidZoomAccount", { type: "boolean" });
};

const validateLinks: ValidationCallback<RawApiOutput["links"]> = (
	input,
	{ flag, checkField },
	auth
) => {
	checkField("hasLinkedZoomAccount", { type: "boolean" });
};

const validateProfileData: ValidationCallback<RawApiOutput["profileData"]> = (
	input,
	{ flag, checkField },
	auth
) => {
	checkField("icalKey", { type: "string" });
	checkField("isMentor", { type: "boolean" });
	checkField("locationCountryName", { type: "string" });
	validate(input, {
		doValidate: validateTerms,
		withErrors: errors => {
			flag("terms", `terms: ${errors.terms ? errors.terms : `unknown error`}`);
		},
		auth
	});
};

const flagValidationErrors: ValidationCallback<RawApiOutput> = (
	input,
	{ flag, checkField },
	auth
) => {
	validate(input.profileData, {
		doValidate: validateProfileData,
		auth,
		withErrors: errors => {
			flag("profileData", JSON.stringify(errors));
		}
	});

	validate(input.links, {
		doValidate: validateLinks,
		auth,
		withErrors: errors => {
			flag("links", JSON.stringify(errors));
		}
	});

	validate(input.settings, {
		doValidate: validateSettings,
		auth,
		withErrors: errors => {
			flag("settings", JSON.stringify(errors));
		}
	});
};

export const getAccountStatus = makeFetchData(
	"accountStatus",
	flagValidationErrors,
	undefined,
	result =>
		result
			? {
					...result.settings,
					...result.links,
					...result.profileData
			  }
			: undefined,
	{ waitForTimezone: false }
);
